import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ShieldAlert, PauseCircle, PlayCircle, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../App';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../ui/alert-dialog';
import { formatDateTimeET } from '../../../utils/formatters';

const API = process.env.REACT_APP_BACKEND_URL;
const MIN_REASON = 10;

const QC_STATUS_LABEL = { pending_review: 'Pending Review (open)', pending: 'Pending (open)', escalated: 'Escalated (open)', returned: 'Returned', rejected: 'Rejected', cancelled: 'Cycle Cancelled' };

const ActionDialog = ({ open, mode, row, onClose, onDone }) => {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setReason(''); }, [open]);
  const isRelease = mode === 'release';
  const submit = async () => {
    setBusy(true);
    try {
      const url = `${API}/api/pricing/billing/qc-gated/${row.id}/${isRelease ? 'release' : 'hold'}`;
      await axios.post(url, { reason: reason.trim() });
      toast.success(isRelease ? `${row.order_number} released to Billing (QC bypassed — not a QC approval)` : `${row.order_number} placed on hold`);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Failed to ${mode}`);
    } finally { setBusy(false); }
  };
  return (
    <AlertDialog open={open} onOpenChange={(v) => !v && onClose()}>
      <AlertDialogContent data-testid={`qc-gated-${mode}-dialog`}>
        <AlertDialogHeader>
          <AlertDialogTitle>{isRelease ? 'Admin exception — Release to Billing?' : 'Place on Hold?'}</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-2 text-sm">
              <p>
                {isRelease
                  ? <>This records a <strong>bypassed</strong> QC disposition (<code>manual_billing_release</code>) so <strong>{row?.order_number}</strong> can be invoiced. <strong>It is not a QC approval</strong> and is written to the billing audit log and the order history.</>
                  : <>Keeps <strong>{row?.order_number}</strong> out of Billing while it is investigated. Release stays blocked until the hold is removed.</>}
              </p>
              <textarea
                data-testid={`qc-gated-${mode}-reason`}
                className="w-full border rounded-md p-2 text-sm min-h-[80px]"
                placeholder={`Reason (minimum ${MIN_REASON} characters, required)`}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                maxLength={500}
              />
              <p className="text-xs text-slate-500">{reason.trim().length}/{MIN_REASON} minimum</p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid={`qc-gated-${mode}-cancel`} disabled={busy}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            data-testid={`qc-gated-${mode}-confirm`}
            disabled={busy || reason.trim().length < MIN_REASON}
            onClick={(e) => { e.preventDefault(); submit(); }}
            className={isRelease ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-amber-600 hover:bg-amber-700'}
          >
            {busy ? 'Saving…' : (isRelease ? 'Release to Billing' : 'Place on Hold')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default function QcGatedQueuePanel({ onClose, onChanged }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = (user?.role || '').toLowerCase() === 'admin';
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialog, setDialog] = useState({ open: false, mode: 'release', row: null });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/pricing/billing/qc-gated?limit=500`);
      setRows(res.data?.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load QC-gated orders');
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const unhold = async (row) => {
    try {
      await axios.delete(`${API}/api/pricing/billing/qc-gated/${row.id}/hold`);
      toast.success(`Hold removed for ${row.order_number}`);
      load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to remove hold'); }
  };
  const done = () => { setDialog((d) => ({ ...d, open: false })); load(); onChanged?.(); };
  const dollars = rows.reduce((s, r) => s + (Number(r.billing_amount) || 0), 0);

  return (
    <div className="bg-white rounded-xl border border-orange-200 overflow-hidden mb-4" data-testid="qc-gated-queue">
      <div className="px-6 py-3 border-b border-slate-100 bg-orange-50/40 flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
            <ShieldAlert className="w-4 h-4 text-orange-600" /> Completed but QC-Gated
            <span className="text-xs font-normal text-slate-500" data-testid="qc-gated-summary">{rows.length} order{rows.length === 1 ? '' : 's'} · ${dollars.toFixed(2)}</span>
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Safety monitor: completed orders that entered QC without a final QC approval. Under the corrected workflow this list should stay empty for new orders — any row is a defect signal.
            Preferred remediation is the QC path (reopen → resubmit → approve). <strong>Release</strong> is an Admin-only, reason-required exception recorded as <em>bypassed</em> (never a QC approval); Accounting may <strong>Hold</strong>.
          </p>
        </div>
        <Button variant="ghost" size="sm" onClick={onClose} className="text-slate-600" data-testid="qc-gated-close">Close</Button>
      </div>
      <div className="overflow-x-auto">
        {loading ? (
          <div className="px-6 py-8 text-center text-sm text-slate-500">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="px-6 py-8 text-center text-sm text-slate-500" data-testid="qc-gated-empty">No QC-gated completed orders. Billing eligibility is clean.</div>
        ) : (
          <table className="w-full text-sm" data-testid="qc-gated-table">
            <thead className="tf-thead text-xs">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Order #</th>
                <th className="text-left px-4 py-2 font-medium">Client</th>
                <th className="text-left px-4 py-2 font-medium">Product</th>
                <th className="text-left px-4 py-2 font-medium">Completed</th>
                <th className="text-left px-4 py-2 font-medium">QC State</th>
                <th className="text-left px-4 py-2 font-medium">Cause</th>
                <th className="text-left px-4 py-2 font-medium">Delivery</th>
                <th className="text-right px-4 py-2 font-medium">Amount</th>
                <th className="text-left px-4 py-2 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`qc-gated-row-${r.id}`}>
                  <td className="px-4 py-2 font-medium whitespace-nowrap">
                    <button className="text-blue-700 hover:underline inline-flex items-center gap-1" onClick={() => navigate(`/orders/${r.id}/overview?from=billing`)} data-testid={`qc-gated-open-${r.id}`}>
                      {r.order_number} <ExternalLink className="w-3 h-3" />
                    </button>
                    {r.hold?.active && <Badge variant="outline" className="ml-2 border-amber-400 text-amber-700 bg-amber-50" data-testid={`qc-gated-hold-badge-${r.id}`} title={r.hold.reason}>On hold</Badge>}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap">{r.client_code || r.client_name || '—'}</td>
                  <td className="px-4 py-2">{r.client_product || '—'}<span className="ml-1 text-[11px] text-slate-400 uppercase">{r.workflow}</span></td>
                  <td className="px-4 py-2 whitespace-nowrap text-xs">{r.completed_at ? formatDateTimeET(r.completed_at) : '—'}<div className="text-slate-500">{r.completed_by_name || ''}</div></td>
                  <td className="px-4 py-2 whitespace-nowrap"><Badge variant="outline" className="border-orange-300 text-orange-800 bg-orange-50" data-testid={`qc-gated-status-${r.id}`}>{QC_STATUS_LABEL[r.qc_status] || r.qc_status}</Badge></td>
                  <td className="px-4 py-2 text-xs text-slate-600 max-w-[320px]" data-testid={`qc-gated-cause-${r.id}`}>{r.cause}</td>
                  <td className="px-4 py-2 text-xs whitespace-nowrap">{r.delivery_status || '—'}</td>
                  <td className="px-4 py-2 text-right tabular-nums font-medium">${Number(r.billing_amount || 0).toFixed(2)}</td>
                  <td className="px-4 py-2 whitespace-nowrap">
                    <div className="flex items-center gap-1">
                      {isAdmin && (
                        <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white" disabled={r.hold?.active}
                          title={r.hold?.active ? 'Remove the hold before releasing' : 'Admin exception: release to Billing (records a bypassed disposition — not a QC approval)'}
                          onClick={() => setDialog({ open: true, mode: 'release', row: r })} data-testid={`qc-gated-release-${r.id}`}>Release to Billing</Button>
                      )}
                      {r.hold?.active ? (
                        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => unhold(r)} data-testid={`qc-gated-unhold-${r.id}`}><PlayCircle className="w-3.5 h-3.5 mr-1" />Remove Hold</Button>
                      ) : (
                        <Button size="sm" variant="outline" className="h-7 text-xs border-amber-300 text-amber-800" onClick={() => setDialog({ open: true, mode: 'hold', row: r })} data-testid={`qc-gated-hold-${r.id}`}><PauseCircle className="w-3.5 h-3.5 mr-1" />Hold</Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {dialog.row && <ActionDialog open={dialog.open} mode={dialog.mode} row={dialog.row} onClose={() => setDialog((d) => ({ ...d, open: false }))} onDone={done} />}
    </div>
  );
}
